import { useState } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { radioProfiles, type ScheduleEntry } from '@/mocks/radioProfile';
import { programs, type Program, type ProgramHost } from '@/mocks/programs';

interface RadioProgramasSectionProps {
  station: RadioStation;
}

const stationToProgramIds: Record<number, number[]> = {
  0: [2, 11],
  1: [2, 10],
  2: [12],
  3: [],
  4: [1, 6],
  5: [],
  7: [3],
  9: [2, 10],
  12: [],
  14: [5],
  16: [],
  19: [7],
};

export default function RadioProgramasSection({ station }: RadioProgramasSectionProps) {
  const profile = radioProfiles[station.id];

  if (profile && profile.schedule.length > 0) {
    return <ScheduleBasedSection stationName={station.name} schedule={profile.schedule} />;
  }

  const mappedIds = stationToProgramIds[station.id] || [];
  const stationPrograms = mappedIds.length > 0
    ? mappedIds.map((id) => programs.find((p) => p.id === id)).filter(Boolean) as Program[]
    : [];

  if (stationPrograms.length > 0) {
    return <ProgramGrid programs={stationPrograms} />;
  }

  const categoryMatch = programs.filter((p) => {
    if (station.category === 'Cristiana') return p.category === 'Salud';
    if (station.category === 'Comunitaria') return p.category === 'Cultura';
    if (station.category === 'Electrónica') return p.category === 'Música';
    if (station.category === 'Clásica') return p.category === 'Cultura';
    if (station.category === 'Regional Mexicana') return p.category === 'Música' || p.category === 'Comedia';
    return false;
  }).slice(0, 3);

  if (categoryMatch.length > 0) {
    return <ProgramGrid programs={categoryMatch} />;
  }

  return null;
}

function ScheduleBasedSection({ stationName, schedule }: { stationName: string; schedule: ScheduleEntry[] }) {
  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Programación</h3>
        <span className="text-xs font-semibold text-foreground-500 flex items-center gap-1">
          <i className="ri-calendar-check-line"></i>
          {schedule.length} programas
        </span>
      </div>
      <div className="divide-y divide-background-200/70">
        {schedule.map((entry, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-3 hover:bg-background-100/50 transition-colors">
            <div className="flex flex-col items-center justify-center min-w-[90px] shrink-0">
              <span className="text-xs font-semibold text-foreground-600 whitespace-nowrap">{entry.day}</span>
              <span className="text-sm font-bold text-foreground-950 whitespace-nowrap">{entry.timeRange}</span>
            </div>
            <div className="w-px self-stretch bg-background-200/70"></div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-bold text-foreground-950">{entry.program}</h4>
              <p className="text-xs text-foreground-500">con {entry.host}</p>
            </div>
            <button className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-semibold text-foreground-600 hover:bg-background-200/70 cursor-pointer transition-colors whitespace-nowrap">
              <i className="ri-notification-3-line"></i>
              Recordar
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProgramGrid({ programs: programList }: { programs: Program[] }) {
  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Programas</h3>
        <button className="text-xs font-semibold text-foreground-500 hover:text-foreground-700 cursor-pointer transition-colors flex items-center gap-1 whitespace-nowrap">
          Ver todos <i className="ri-arrow-right-line"></i>
        </button>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {programList.map((program) => (
          <ProgramCard key={program.id} program={program} />
        ))}
      </div>
    </section>
  );
}

function ProgramCard({ program }: { program: Program }) {
  const [following, setFollowing] = useState(false);
  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  return (
    <div className="rounded-xl border border-background-200/70 overflow-hidden bg-background-50 hover:border-background-300/60 transition-all group">
      <div className="relative h-36 bg-background-200/70 overflow-hidden">
        <img
          src={program.coverUrl}
          alt={program.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
        {program.isActive && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold whitespace-nowrap">
            <span className="w-1 h-1 rounded-full bg-white"></span>
            Activo
          </div>
        )}
        {!program.isActive && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-foreground-400 text-white text-[10px] font-bold whitespace-nowrap">
            Inactivo
          </div>
        )}
        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px]">
          {program.schedule}
        </div>
      </div>

      <div className="p-3">
        <h4 className="text-sm font-bold text-foreground-950 line-clamp-1">{program.name}</h4>
        <p className="text-xs text-foreground-500 mt-0.5 line-clamp-2 leading-relaxed">{program.description}</p>

        <div className="flex items-center gap-2 mt-2.5">
          {program.hosts.slice(0, 2).map((host) => (
            <div key={host.id} className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded-full bg-background-200/70 overflow-hidden">
                <img
                  src={host.photoUrl}
                  alt={host.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-[11px] text-foreground-600 font-medium truncate">{host.name}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-[11px] text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i> {formatNumber(program.followers)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i> {formatNumber(program.community.comments)}
            </span>
          </div>
          <button
            onClick={() => setFollowing(!following)}
            className={`text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap px-3 py-1 rounded-full ${
              following
                ? 'bg-background-100 text-foreground-600 border border-background-200/70'
                : 'bg-secondary-500 text-background-50 dark:text-foreground-950 hover:bg-secondary-600'
            }`}
          >
            {following ? 'Siguiendo' : 'Seguir programa'}
          </button>
        </div>
      </div>
    </div>
  );
}